import api from '@/services/api'
import denormalizeResponse from '@/utils/denormalizeResponse'
import router from "@/router";
import {setContact} from "./mutations";

async function requestResource(type, id, query) {
    const data = await api.get(`/api/v2/resources/${type}/${id}`, { query })

    return denormalizeResponse(data).root
}

export async function changeActiveStep({ commit, state }, step) {
    if(step !== 0 && !state.hasStartedRostering) {
        await router.push({ name: 'Roster/Event/TeamWelcome' })
        return
    }

    commit('changeActiveStep', { step })
}

export async function setCurrentTeam({commit}, teamId) {
    commit('changeCurrentTeamId', { teamId })
}

export async function retrieveEventId({ commit }, token) {
    try {
        const { eventId } = await api.get('/api/v1/rostering/event-id', { token })
        commit('retrieveEventIdSuccess', { eventId })
    } catch (e) {
        commit('retrieveEventIdError')
    }
}

export async function retrieveEvent({ commit }, eventId) {

    const event = await requestResource('events', eventId, {
        include: {
            name: true,
            locationCity: true,
            locationState: true,
            startDate: true,
            endDate: true,
            isUsaNumberRequired: true,
            images: {
                url: true,
                isProfile: true,
            },
            sport: {
                name: true,
                positions: true,
                position_aliases: true,
                rosteringFields: true,
            },
            gender: true,
            teams: {
                '@from': 'teamsWithoutPlaceholder',
                name: true,
                division: {
                    name: true,
                },
                isRostered: true,
            }
        },
    })

    commit('retrieveEventSuccess', { event })

}

export async function submitRoster({commit,dispatch,state}) {
    const token = router.currentRoute.params.token
    const eventId = state.eventId
    let success = true
    try {
        if (state.auth.nonIntegratedIdentity) {
            await dispatch('registerContact', {eventId: eventId, token: token})
        }

        await dispatch('registerRoster', {eventId: eventId, token: token})
        await dispatch('registerCoaches', {eventId: eventId, token: token})
    }catch(e) {
        console.error(e)
        success = false
    }
    return success
}

export async function registerContact({state}, {eventId, token}) {
    const firstName = state.auth.nonIntegratedIdentity.firstName
    const lastName = state.auth.nonIntegratedIdentity.lastName
    const phone = state.auth.nonIntegratedIdentity.phone
    const email = state.auth.nonIntegratedIdentity.email
    const roleId = state.auth.nonIntegratedIdentity.roleId

    await api.post(`/api/v1/rostering/${eventId}/${token}/register`, {
        firstName,
        lastName,
        email,
        phone,
        roleId,
    })
}

export async function registerRoster({state}, {eventId, token}) {
    const identity = state.auth.nonIntegratedIdentity
    const entries = state.roster
    const teamId = state.currentTeamId

    await api.post(`/api/v1/rostering/${eventId}/${token}/${teamId}/submit`, {
        identity: identity,
        athletes: entries.map(entry => {
            return ({
                id: entry.id,
                firstName: entry.firstName,
                lastName: entry.lastName,
                email: entry.email,
                gradYear: entry.gradYear,
                guardianEmail1: entry.guardianEmail1,
                guardianEmail2: entry.guardianEmail2,
                phoneNumber: entry.phoneNumber,
                jerseyNumber: entry.jerseyNumber,
                positions: entry.positions,
                preCommitted: entry.preCommitted,
                ...(state.event.sport.rosteringFields || [])
                    .reduce((sportFields, field) => {
                        sportFields[field.field_name] = entry[field.field_name]
                        return sportFields;
                    }, {})
            })
        })
    })
}

export async function registerCoaches({state}, {eventId, token}) {
    const teamId = state.currentTeamId
    const coaches = state.coaches
    if(coaches.length) {
        await api.post(`/api/v1/rostering/${eventId}/${token}/${teamId}/coaches/register`, {coaches: coaches})
    }
}

export function initTableAthletes({ commit }, entries) {
    commit('initTableAthletes', entries)
}
export function saveRoster({ commit }, athletes) {
    commit('saveRoster', athletes)
}

export function saveCoaches({ commit }, coaches) {
    commit('saveCoaches', coaches)
}

export async function startRostering({ commit }) {
    commit('startRostering')
}

export async function retrieveRoles({ commit }) {
    const { roles } = await api.get("/api/v1/rostering/roles");
    await commit('setRoles', { roles: roles })
}


export async function saveContact({ commit }, { firstName, lastName, email, phone, roleId }) {
        commit('setContact', {
            identity: {
                firstName,
                lastName,
                email,
                phone,
                roleId,
            }
        })
}

export function setRosterError({ commit }, {rowId, fieldName, message, isRequired = false}) {
    commit('setRosterError', {
        rowId: rowId,
        fieldName: fieldName,
        message: message,
        isRequired: isRequired
    })
}

export function removeRosterError({ state, commit }, {rowId, fieldName, isRequired = false}) {
    if(state.rosterErrors[rowId] && state.rosterErrors[rowId][fieldName]) {
        commit('removeRosterError', {
            rowId: rowId,
            fieldName: fieldName,
            isRequired: isRequired
        })
    }
}

export function getRosterError({ state }, {rowId, fieldName}) {
    if(!state.rosterErrors[rowId]) {
        return false
    }
    return state.rosterErrors[rowId][fieldName] ?? false
}

export function setPrimaryEmail({ commit }, {email, position}) {
    commit('setPrimaryEmail', {email: email, position: position})
}

export function resetErrors({ commit }) {
    commit('resetErrors')
}

export function resetRowErrors({commit}, row) {
    commit('resetRowErrors', {row:row})
}

export function validateRoster({commit}) {
    commit('validateRoster')
}

export function invalidateRoster({commit}) {
    commit('invalidateRoster')
}

export function resetWorkspace({commit}) {
    commit('resetWorkspace')
}

export function resetRoster({commit}) {
    commit('resetRoster')
}

export async function updateTeamStatus({ commit, getters }) {
    commit('updateTeamStatus', getters.currentTeam.id)
}

export async function setIntegratedIdentity({ commit }, identity) {
    commit('setIntegratedIdentity', identity)
}

export async function setNonIntegratedIdentity({ commit }, identity) {
    commit('setContact', identity)
}

export async function setMyAccount({ commit }, account) {
    commit('setMyAccount', {
        id: account.id,
        organization: {
            id: account.organization.id,
            name: account.organization.name,
            gender: account.organization.gender,
            athletes: [],
            teams: [],
            sport: account.organization.sport
        }
    });
}

export async function setAthletes({ commit, state }, athletes) {
    for (const athlete of athletes) {
        // @TODO: Need to ensure we are getting the correct jersey number
        const teamAffiliation = athlete.teamAffiliations.filter(aff => !!aff.team).find(aff => aff.team.organization.id && aff.jerseyNumber);
        const organizationAffiliation = [ ...athlete.clubAffiliations, ...athlete.highSchoolAffiliations ].find(aff => aff.organization.id === state.me.organization.id);

        commit('addAthlete', {
                id: athlete.id,
                firstName: athlete.firstName,
                lastName: athlete.lastName,
                email: athlete.emailAddress,
                phoneNumber: athlete.phoneNumber,
                gradYear: athlete.classYear?.toString(),
                jerseyNumber: teamAffiliation?.jerseyNumber || organizationAffiliation?.jerseyNumber || "",
                positions: athlete.positions || [],
                preCommitted: "",
                guardianEmail1: athlete.guardians && athlete.guardians.length > 0 ? athlete.guardians[0].emailAddress : "",
                guardianEmail2: athlete.guardians && athlete.guardians.length > 1 ? athlete.guardians[1].emailAddress : "",
                teams: athlete.teamAffiliations.filter(aff => !!aff.team).map(affiliation => ({ id: affiliation.team.id, name: affiliation.team.name })),
                ...athlete.athleticStats.reduce((sportFields, athleteStats) => { sportFields[athleteStats.slug] = athleteStats.value; return sportFields; }, {}),
            }
        )
    }
}

export async function setTeams({ commit }, teams) {
    for (const team of teams) {
        commit('addTeam', { 
            id: team.id, 
            name: team.name,
            athletes: team.athletes
        })
    }
}